import { doc, updateDoc, getDoc, getDocs, collection, query, where, limit, runTransaction } from 'firebase/firestore';
import { db } from '../config/firebase';
import { BillingStatus, PackageTier } from '../types/billing';
import {
    BESTAND_MANUAL_REVIEW_MSG,
    matchBestandAgainstDocs,
    normalizeCustomerNumberCore,
} from '../utils/bestandMatch';

export function isBillingPending(billingStatus?: BillingStatus): boolean {
    return billingStatus === 'pending_approval';
}

function isBestandLike(data: Record<string, any>): boolean {
    return !!(
        data?.isExistingCustomer ||
        data?.isBestandskunde ||
        data?.clientData?.isExistingCustomer ||
        data?.clientData?.isBestandskunde ||
        data?.isLegacy ||
        data?.clientData?.isLegacy
    );
}

export async function simulateStripeActivation(
    mandantId: string,
    packageTier: PackageTier
): Promise<{ success: boolean; mockSubId: string }> {
    const mockSubId = `sub_mock_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;
    const mandantRef = doc(db, 'kalkulation_mandanten', mandantId);

    const snap = await getDoc(mandantRef);
    const data = snap.data() || {};
    const isExisting = isBestandLike(data);

    const now = new Date();
    const core = `${String(now.getMonth() + 1).padStart(2, '0')}/${now.getFullYear()}`;
    const rawNr = String(data.mandantNr || data.clientData?.mandantNr || '').trim();
    const needsNrRepair =
        !rawNr ||
        rawNr === 'WIEDERHERGESTELLT' ||
        rawNr === '---' ||
        /^warte/i.test(rawNr);

    let repairedNr = rawNr;
    if (needsNrRepair) {
        repairedNr = `MA - ${core} - ${packageTier}`;
    } else if (/MA\s+-\s+\d{2}\/\d{4}/i.test(rawNr)) {
        repairedNr =
            rawNr.replace(/\s+(Solo|TeamBase|TeamPro\+|TeamPro)\s*$/i, '').trim() +
            ` - ${packageTier}`;
    }

    // Bestand: echte Kd.-Nr. (22/2022) behalten — keine neue vergeben
    const existingKd = String(
        data.customerNumber ||
            data.existingCustomerNumber ||
            data.providedOldId ||
            data.clientData?.customerNumber ||
            ''
    ).trim();
    const kdCore = normalizeCustomerNumberCore(existingKd);
    const keepCustomerNumber = existingKd && existingKd !== 'PENDING' ? existingKd : '';

    const updatePayload: Record<string, any> = {
        billingStatus: 'active',
        packageTier,
        tarifStatus: packageTier,
        isSolo: packageTier === 'Solo',
        hasEmployeeEval: packageTier === 'TeamPro' || packageTier === 'TeamPro+',
        stripeSubscriptionId: mockSubId,
        billingActivatedAt: new Date().toISOString(),
        mandantNr: repairedNr,
        'clientData.mandantNr': repairedNr,
        'clientData.packageTier': packageTier,
        'clientData.tarifStatus': packageTier,
        'clientData.billingStatus': 'active',
        'clientData.isSolo': packageTier === 'Solo',
        'clientData.hasEmployeeEval': packageTier === 'TeamPro' || packageTier === 'TeamPro+',
        // Keine zweite Freigabe-Mail durch Billing-Felder (onMandantUpdated Silent-Mode)
        lastSilentAdminUpdate: Date.now(),
    };

    if (!data.vertragsbeginn && !data.clientData?.vertragsbeginn) {
        updatePayload.vertragsbeginn = core;
        updatePayload['clientData.vertragsbeginn'] = core;
    }

    // Vorab/Bestand → Sentinel: Lastschrift bei Bank abschalten
    if (isExisting) {
        updatePayload.sepaCancelPending = true;
        updatePayload.isLegacy = false;
        updatePayload['clientData.isLegacy'] = false;
        if (keepCustomerNumber) {
            updatePayload.customerNumber = keepCustomerNumber;
            updatePayload.existingCustomerNumber =
                kdCore || String(data.existingCustomerNumber || keepCustomerNumber);
            updatePayload['clientData.customerNumber'] = keepCustomerNumber;
        }
    }

    await updateDoc(mandantRef, updatePayload);

    return { success: true, mockSubId };
}

/** Coach-Freischaltung: Vorabakte per E-Mail / Kd.-Nr. finden. */
export async function resolveBestandMatchForActivation(
    email: string,
    customerNumberRaw: string
): Promise<{
    matched: boolean;
    needsManualReview: boolean;
    message?: string;
    monthlyFee: number | null;
    vorabId: string | null;
}> {
    const emailNorm = String(email || '').trim().toLowerCase();
    const core = normalizeCustomerNumberCore(customerNumberRaw);
    const col = collection(db, 'kalkulation_mandanten');
    const docs: Array<{ id: string; data: any }> = [];
    const seen = new Set<string>();

    const pushSnaps = (snaps: Awaited<ReturnType<typeof getDocs>>) => {
        snaps.forEach((d) => {
            if (seen.has(d.id)) return;
            seen.add(d.id);
            docs.push({ id: d.id, data: d.data() });
        });
    };

    try {
        if (emailNorm) {
            pushSnaps(await getDocs(query(col, where('email', '==', emailNorm), limit(10))));
            pushSnaps(await getDocs(query(col, where('mailadresse', '==', emailNorm), limit(10))));
            const rawEmail = String(email || '').trim();
            if (rawEmail && rawEmail !== emailNorm) {
                pushSnaps(await getDocs(query(col, where('email', '==', rawEmail), limit(10))));
                pushSnaps(await getDocs(query(col, where('mailadresse', '==', rawEmail), limit(10))));
            }
        }
        if (core) {
            pushSnaps(await getDocs(query(col, where('customerNumber', '==', core), limit(10))));
            pushSnaps(await getDocs(query(col, where('existingCustomerNumber', '==', core), limit(10))));
        }
        pushSnaps(await getDocs(query(col, where('isLegacy', '==', true), limit(80))));
    } catch (e) {
        console.warn('Bestand-Match-Query:', e);
    }

    const result = matchBestandAgainstDocs(email, customerNumberRaw, docs);
    if (result.matched) {
        return {
            matched: true,
            needsManualReview: false,
            monthlyFee: result.monthlyFee,
            vorabId: result.vorabId,
        };
    }
    return {
        matched: false,
        needsManualReview: true,
        message: BESTAND_MANUAL_REVIEW_MSG,
        monthlyFee: result.monthlyFee,
        vorabId: result.vorabId,
    };
}

export async function allocateNextCustomerNumber(mandantId: string): Promise<string> {
    const currentYear = new Date().getFullYear();
    const counterRef = doc(db, 'system_data', `customer_counter_${currentYear}`);
    const mandantRef = doc(db, 'kalkulation_mandanten', mandantId);

    return await runTransaction(db, async (transaction) => {
        const counterDoc = await transaction.get(counterRef);
        let nextCount = 1;

        if (counterDoc.exists()) {
            nextCount = (counterDoc.data().lastCount || 0) + 1;
        }

        const formattedNumber = `${String(nextCount).padStart(4, '0')}/${currentYear}`;

        transaction.set(counterRef, { lastCount: nextCount, updatedAt: new Date().toISOString() }, { merge: true });
        transaction.update(mandantRef, {
            customerNumber: formattedNumber,
            lastSilentAdminUpdate: Date.now(),
        });

        return formattedNumber;
    });
}
